import React, { useState } from 'react';
import {
  Card,
  Typography,
  Table,
  Tag,
  Button,
  Modal,
  Select,
  Space,
  message,
  Row,
  Col,
  Statistic,
  Spin,
  Tooltip,
  Input,
  Descriptions,
} from 'antd';
import {
  DollarOutlined,
  RollbackOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../services/api';
import dayjs from 'dayjs';

const { Title } = Typography;

const Refunds = () => {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [selectedRefund, setSelectedRefund] = useState(null);
  const [isDetailVisible, setIsDetailVisible] = useState(false);
  const [isStatusUpdateVisible, setIsStatusUpdateVisible] = useState(false);

  const { data: refundsData, isLoading } = useQuery(
    ['refunds', searchTerm, statusFilter, page],
    async () => {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (statusFilter) params.append('status', statusFilter);
      params.append('page', page);
      params.append('per_page', '15');

      const response = await api.get(`/admin/refunds?${params.toString()}`);
      return response.data;
    },
    {
      onError: () => message.error('Failed to load refunds'),
    },
  );

  const { data: refundStats } = useQuery(
    'refund-stats',
    async () => {
      const response = await api.get('/admin/refunds/stats');
      return response.data?.data || {};
    },
    {
      onError: () => {},
    },
  );

  const updateStatusMutation = useMutation(
    async ({ refundId, status }) => {
      const response = await api.put(`/admin/refunds/${refundId}/status`, { status });
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Refund status updated');
        queryClient.invalidateQueries('refunds');
        queryClient.invalidateQueries('refund-stats');
        setIsStatusUpdateVisible(false);
        setSelectedRefund(null);
      },
      onError: (error) => {
        message.error(error.response?.data?.message || 'Failed to update status');
      },
    },
  );

  const getRefundTypeColor = (type) => {
    const map = { full: 'red', partial: 'orange', item_level: 'blue' };
    return map[type] || 'default';
  };

  const getStatusColor = (status) => {
    const map = { processed: 'green', pending: 'orange', failed: 'red' };
    return map[status] || 'default';
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 70,
    },
    {
      title: 'Order',
      key: 'order',
      width: 120,
      render: (_, record) => `#${record.order?.id || record.order_id}`,
    },
    {
      title: 'Type',
      dataIndex: 'refund_type',
      key: 'refund_type',
      width: 120,
      render: (type) => (
        <Tag color={getRefundTypeColor(type)}>
          {type === 'full' ? 'Full' : type === 'partial' ? 'Partial' : 'Item Level'}
        </Tag>
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'refund_amount',
      key: 'refund_amount',
      width: 120,
      render: (amount) => `Rs. ${parseFloat(amount || 0).toFixed(2)}`,
      sorter: (a, b) => parseFloat(a.refund_amount || 0) - parseFloat(b.refund_amount || 0),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (status) => (
        <Tag
          color={getStatusColor(status)}
          icon={
            status === 'processed' ? (
              <CheckCircleOutlined />
            ) : status === 'pending' ? (
              <ClockCircleOutlined />
            ) : (
              <CloseCircleOutlined />
            )
          }
        >
          {status?.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Reason',
      dataIndex: 'reason',
      key: 'reason',
      ellipsis: true,
      render: (reason) => reason || '-',
    },
    {
      title: 'Admin',
      key: 'admin',
      width: 130,
      render: (_, record) => record.admin?.name || record.admin?.full_name || '-',
    },
    {
      title: 'Date',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 120,
      render: (date) => (date ? dayjs(date).format('MMM D, YYYY') : '-'),
      sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View Details">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => {
                setSelectedRefund(record);
                setIsDetailVisible(true);
              }}
            />
          </Tooltip>
          {record.status !== 'processed' && (
            <Tooltip title="Update Status">
              <Button
                type="text"
                icon={<RollbackOutlined />}
                onClick={() => {
                  setSelectedRefund(record);
                  setIsStatusUpdateVisible(true);
                }}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  const refunds = Array.isArray(refundsData?.data) ? refundsData.data : [];
  const pagination = refundsData?.pagination || {};

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <Title level={3}>
        <RollbackOutlined /> Refund Management
      </Title>

      {/* Stats Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Refunds"
              value={refundStats?.total_refunds || 0}
              prefix={<RollbackOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Amount"
              value={refundStats?.total_refund_amount || 0}
              prefix={<DollarOutlined />}
              precision={2}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Processed"
              value={refundStats?.processed_refunds || 0}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Pending"
              value={refundStats?.pending_refunds || 0}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Refunds Table */}
      <Card
        title="All Refunds"
        extra={
          <Space>
            <Input.Search
              placeholder="Search refunds..."
              allowClear
              style={{ width: 200 }}
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1);
              }}
            />
            <Select
              placeholder="Filter by status"
              allowClear
              style={{ width: 150 }}
              onChange={(value) => {
                setStatusFilter(value || '');
                setPage(1);
              }}
              options={[
                { label: 'Processed', value: 'processed' },
                { label: 'Pending', value: 'pending' },
                { label: 'Failed', value: 'failed' },
              ]}
            />
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={refunds}
          rowKey="id"
          pagination={{
            current: pagination.current_page || page,
            pageSize: pagination.per_page || 15,
            total: pagination.total || 0,
            onChange: setPage,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} refunds`,
          }}
          scroll={{ x: 1000 }}
        />
      </Card>

      {/* Detail Modal */}
      <Modal
        title={
          <span>
            <RollbackOutlined /> Refund Details #{selectedRefund?.id}
          </span>
        }
        open={isDetailVisible}
        onCancel={() => {
          setIsDetailVisible(false);
          setSelectedRefund(null);
        }}
        footer={[
          <Button
            key="close"
            onClick={() => {
              setIsDetailVisible(false);
              setSelectedRefund(null);
            }}
          >
            Close
          </Button>,
        ]}
        width={700}
      >
        {selectedRefund &&
          (() => {
            const order = selectedRefund.order || {};
            const customer = order.customer || {};
            const orderItem = selectedRefund.order_item || {};
            const product = orderItem.product || {};
            const invoice = order.invoices?.[0] || {};
            const unitPrice = orderItem.price_at_order || product.price || 0;
            const originalQty = orderItem.quantity || 0;
            const refundAmount = parseFloat(selectedRefund.refund_amount || 0);
            const refundedQty = unitPrice > 0 ? Math.round(refundAmount / unitPrice) : 0;

            return (
              <>
                {/* Refund Summary */}
                <Descriptions column={2} bordered size="small" style={{ marginTop: 16 }}>
                  <Descriptions.Item label="Refund ID" span={2}>
                    #{selectedRefund.id}
                  </Descriptions.Item>
                  <Descriptions.Item label="Status">
                    <Tag color={getStatusColor(selectedRefund.status)}>
                      {selectedRefund.status_label || selectedRefund.status?.toUpperCase()}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Type">
                    <Tag color={getRefundTypeColor(selectedRefund.refund_type)}>
                      {selectedRefund.type_label || selectedRefund.refund_type}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Refund Amount" span={2}>
                    <span style={{ color: '#cf1322', fontWeight: 600, fontSize: 16 }}>
                      Rs. {refundAmount.toFixed(2)}
                    </span>
                  </Descriptions.Item>
                </Descriptions>

                {/* Customer Info */}
                <Title level={5} style={{ marginTop: 24, marginBottom: 8 }}>
                  Customer
                </Title>
                <Descriptions column={2} bordered size="small">
                  <Descriptions.Item label="Name">
                    {customer.name || customer.full_name || customer.customer_name || '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Email">
                    {customer.email || customer.customer_email || '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Phone">
                    {customer.phone_number || customer.phone || customer.customer_phone || '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Order">
                    <a href={`/orders/${order.id}`} target="_blank" rel="noreferrer">
                      #{order.id}
                    </a>
                  </Descriptions.Item>
                </Descriptions>

                {/* Order Info */}
                <Title level={5} style={{ marginTop: 24, marginBottom: 8 }}>
                  Order Details
                </Title>
                <Descriptions column={2} bordered size="small">
                  <Descriptions.Item label="Order Date">
                    {order.created_at ? dayjs(order.created_at).format('MMM D, YYYY') : '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Order Status">
                    <Tag>{order.order_status || '-'}</Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Payment Status">
                    <Tag color={order.payment_status === 'paid' ? 'green' : 'orange'}>
                      {order.payment_status || '-'}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Payment Method">
                    {order.payment_method || '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Order Total">
                    Rs. {parseFloat(order.total_amount || 0).toFixed(2)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Delivery Charge">
                    Rs. {parseFloat(order.delivery_charge || 0).toFixed(2)}
                  </Descriptions.Item>
                </Descriptions>

                {/* Refunded Item Details */}
                <Title level={5} style={{ marginTop: 24, marginBottom: 8 }}>
                  Refunded Item
                </Title>
                <Descriptions column={2} bordered size="small">
                  <Descriptions.Item label="Item Name" span={2}>
                    <strong>{selectedRefund.order_item?.item_name || product.name || '-'}</strong>
                  </Descriptions.Item>
                  <Descriptions.Item label="Product Type">
                    <Tag color={product.type === 'service' ? 'purple' : 'blue'}>
                      {(product.type || 'product').toUpperCase()}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Auto Restock">
                    {selectedRefund.auto_restock ? 'Yes' : 'No'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Unit Price">
                    Rs. {parseFloat(unitPrice).toFixed(2)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Original Quantity">{originalQty}</Descriptions.Item>
                  <Descriptions.Item label="Refunded Quantity">{refundedQty}</Descriptions.Item>
                  <Descriptions.Item label="Remaining Quantity">
                    {Math.max(0, originalQty - refundedQty)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Refund Amount">
                    Rs. {refundAmount.toFixed(2)}
                  </Descriptions.Item>
                </Descriptions>

                {/* Invoice Info (if available) */}
                {invoice.id && (
                  <>
                    <Title level={5} style={{ marginTop: 24, marginBottom: 8 }}>
                      Invoice
                    </Title>
                    <Descriptions column={2} bordered size="small">
                      <Descriptions.Item label="Invoice #" span={2}>
                        {invoice.invoice_number || `#${invoice.id}`}
                      </Descriptions.Item>
                      <Descriptions.Item label="Invoice Status">
                        <Tag
                          color={
                            invoice.status === 'paid'
                              ? 'green'
                              : invoice.status === 'sent'
                                ? 'blue'
                                : 'default'
                          }
                        >
                          {invoice.status || '-'}
                        </Tag>
                      </Descriptions.Item>
                      <Descriptions.Item label="Refunded on Invoice">
                        Rs. {parseFloat(invoice.refunded_amount || 0).toFixed(2)}
                      </Descriptions.Item>
                      <Descriptions.Item label="Invoice Total">
                        Rs. {parseFloat(invoice.total_amount || 0).toFixed(2)}
                      </Descriptions.Item>
                    </Descriptions>
                  </>
                )}

                {/* Notes & Audit */}
                <Title level={5} style={{ marginTop: 24, marginBottom: 8 }}>
                  Notes & Audit
                </Title>
                <Descriptions column={1} bordered size="small">
                  <Descriptions.Item label="Reason">
                    {selectedRefund.reason || '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Admin Notes">
                    {selectedRefund.admin_notes || '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Processed By">
                    {selectedRefund.admin?.name || selectedRefund.admin?.full_name || '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Created At">
                    {dayjs(selectedRefund.created_at).format('MMM D, YYYY h:mm A')}
                  </Descriptions.Item>
                  <Descriptions.Item label="Restock Amount">
                    Rs. {parseFloat(selectedRefund.restock_amount || 0).toFixed(2)}
                  </Descriptions.Item>
                </Descriptions>
              </>
            );
          })()}
      </Modal>

      {/* Status Update Modal */}
      <Modal
        title="Update Refund Status"
        open={isStatusUpdateVisible}
        onCancel={() => {
          setIsStatusUpdateVisible(false);
          setSelectedRefund(null);
        }}
        onOk={() => {
          if (selectedRefund) {
            Modal.confirm({
              title: 'Process this refund?',
              content: `Set refund #${selectedRefund.id} to "processed"? This will mark it as completed.`,
              onOk: () =>
                updateStatusMutation.mutate({
                  refundId: selectedRefund.id,
                  status: 'processed',
                }),
            });
          }
        }}
        okText="Mark as Processed"
        okButtonProps={{ danger: true, icon: <CheckCircleOutlined /> }}
      >
        {selectedRefund && (
          <div>
            <p>
              <strong>Refund ID:</strong> #{selectedRefund.id}
            </p>
            <p>
              <strong>Amount:</strong> Rs.{' '}
              {parseFloat(selectedRefund.refund_amount || 0).toFixed(2)}
            </p>
            <p>
              <strong>Current Status:</strong>{' '}
              <Tag color={getStatusColor(selectedRefund.status)}>
                {selectedRefund.status_label || selectedRefund.status?.toUpperCase()}
              </Tag>
            </p>
            <p>
              <strong>Reason:</strong> {selectedRefund.reason || '-'}
            </p>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Refunds;
